import {Avatar} from 'antd'
import {
    comment_addComment_Api,
    comment_addReply_Api,
    comment_getComments_Api,
    comment_getReples_Api,
    commentContainerSchema,
} from 'apis'
import {getId, getType} from 'apis/model/base'
import {socketUnionSchema} from 'apis/schema'
import {commentSchema} from 'apis/schema/commentSchema'
import classNames from 'classnames'
import EmptyHolder from 'components/EmptyHolder'
import {AskWrapper} from 'components/Feed/Ask/AskContentWrapper'
import Description from 'components/Feed/Description'
import {ExtraLike, Like} from 'components/Feed/Like'
import {get_mention_data, getText} from 'components/Feed/MentionDataContext'
import {isExtraVoting, VoteTypes} from 'components/Feed/Vote'
import InputBar from 'components/InputBar'
import {LoginContext} from 'components/LoginContext'
import {NullAsDefault, renderElse, renderOwnChild} from "views/Shared";
import {SelectEntityItem} from 'components/SelectEntityItem'
import getLinkToDetail from 'helpers/getLinkToDetail'
import getTitle from 'helpers/getTitle'
import logFunc from 'helpers/logFunc'
import _ from 'lodash'
import AsyncAction from 'modules/asyncCache/components/AsyncAction'
import {LazyPagination} from 'modules/asyncCache/components/LazyPagination'
import useTranslate from 'modules/local/useTranslate'
import {SocketContext} from 'modules/socket/SocketContext'
import {JOIN_ROOM} from 'modules/socket/types'
import {normalize} from 'normalizr'
import React, {useCallback, useContext, useEffect, useMemo, useState,} from 'react'
import {BsPersonFill, BsReplyFill} from 'react-icons/bs'
import {IoChatboxEllipsesOutline} from 'react-icons/io5'
import {RiUser3Fill} from 'react-icons/ri'
import {useDispatch} from 'react-redux'
import {Link} from 'react-router-dom'
import useToggle from 'react-use/lib/useToggle'
import {nest} from 'recompose'
import {useSelectEntities} from 'redux/entities/useSelectEntities'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {renderLoading} from 'views/MainPage/functions/renderPagingLoading'
import {GoogleAdsRecommendedUnitHorizontal} from '../../components/GoogleAds/GoogleAds'
import {LayoutContext} from '../../components/layouts/Default/LayoutContext'
import {useExtraVote, useVote} from '../Server/ServerContainer'
import {renderIf, renderIfElse} from '../Shared'
import ResponsesContextMenu from './components/ResponsesContextMenu'
import ResponsesEditAction from './components/ResponsesEditAction'
import FeedContext from "../../components/Feed/FeedContext";
import {FiEdit3} from "react-icons/fi";
import {formatSimplifyDate} from "../../helpers/dateUtils";
import {BiSolidLike} from "react-icons/bi";
import {formatNumber} from "../../helpers/number";
import {IoIosChatbubbles} from "react-icons/io";

const Comment = ({item, className, canReply = true}) => {

    const t = useTranslate()
    const login = useContext(LoginContext)
    const {isLg} = useContext(LayoutContext)

    const [openEdit, toggleOpenEdit] = useToggle()
    const [openReplies, toggleOpenReplies] = useToggle()
    const showReplies = !item?.isDeleted && !!canReply
    const isReply = !!item?.parent_id;

    const {creator, is_author, vote_status, total_votes, total_comments, total_replies, id} = item ?? {}
    const handleVote = useVote({
        vote_status,
        total_votes,
        owner_id: id,
        owner_type: getType(item),
    })

    const {avatar, thumbnail} = creator ?? {}

    const isCreator = getId(creator) === getId(login)

    const displayName = (
        renderIfElse(
            isCreator,
            <span className="text-color-100 italic">
                {t('is_me')}
            </span>,
            getTitle(creator)
        )
    )

    const get_decrypted_values = useCallback((values) => {
        const {message, mentions} = values ?? {}
        const description_data = get_mention_data(mentions) ?? undefined
        return {
            ...(values ?? {}),
            description_data,
            message: getText(message, get_mention_data(mentions, true)),
        }
    }, [])

    if (item?.isDeleted) {
        return null
    }

    return (
        <div className={classNames(
            'relative',
            isReply
                ? 'py-2 px-2 rounded-lg hover:background-100'  // reply: nhẹ hơn, ko shadow
                : 'mb-3 p-3 background hover:shadow-md rounded-xl custom-item-render', // comment: đầy đủ
            className,
        )}>
            <div className="flex items-start gap-2">
                <Avatar
                    size={isReply ? 24 : 32}  // avatar nhỏ hơn với reply
                    className="flex flex-center background-100 flex-shrink-0"
                    icon={<BsPersonFill size={isReply ? 13 : 18} className="text-color-400"/>}
                    src={avatar || thumbnail}
                />
                <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-center gap-2">
                        <div className="leading-tight flex flex-col flex-1">
                            <Link
                                rel="nofollow"
                                to={getLinkToDetail(creator)}
                                className="flex items-end gap-2 no-underline">
                                <span className={classNames(
                                    "font-bold",
                                    isReply ? "text-xs" : "text-sm",
                                    is_author && 'italic'
                                )}>
                                    {getTitle(creator)}
                                </span>
                            </Link>
                            <span className="text-2xs text-color-400 italic whitespace-no-wrap tracking-wide">
                                {formatSimplifyDate(item?.created, 'fullTime')}
                            </span>
                        </div>
                        <ResponsesContextMenu item={item} onEdit={toggleOpenEdit}/>
                    </div>

                    {/* Content */}
                    <div className={classNames("pb-1", isReply ? "mt-0.5" : "mt-1")}>
                        {openEdit ? (
                            <ResponsesEditAction onExit={toggleOpenEdit} item={item}/>
                        ) : (
                            <div className="flex flex-col gap-1">
                                <Description
                                    className="leading-tight text-sm"
                                    type="post"
                                    data={item}
                                    mentions={_.get(item, 'mentions')}>
                                    {_.get(item, 'message')}
                                </Description>
                                {!!_.get(item, 'photos', [])?.length && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {_.get(item, 'photos', []).map((photo, photoIndex) => (
                                            <img key={photoIndex} src={photo.path}
                                                 className="rounded object-cover"
                                                 style={{width: 80, height: 80}}/>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Stats */}
                    {_.some([total_votes > 0, total_replies > 0]) && (
                        <div className="flex gap-3 mb-1">
                            {total_votes > 0 && (
                                <div className="flex items-center gap-1">
                                    <Avatar size={16} icon={<BiSolidLike size={10} className="text-white"/>}
                                            className="flex flex-center bg-blue-600"/>
                                    <span className="font-medium text-xs tracking-wider">
                                        {formatNumber(Number(total_votes))}
                                    </span>
                                </div>
                            )}
                            {showReplies && total_replies > 0 && (
                                <div
                                    onClick={toggleOpenReplies}
                                    className="flex items-center gap-1 tracking-wider text-xs italic cursor-pointer">
                                    <BsReplyFill size={12} className="text-color-500"/>
                                    <span className="font-medium text-color-100">{formatNumber(Number(total_replies))}</span>
                                    <span className="text-color-300 hover:text-color-100 hover:underline">
                                        {t('reply').toLowerCase()}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-4">
                        <Like
                            vote_status={item?.vote_status}
                            total_votes={item?.total_votes}
                            handleVote={() => handleVote()}
                        />
                        {canReply && (
                            <div
                                onClick={toggleOpenReplies}
                                className="flex items-center gap-1 cursor-pointer hover:background-200 rounded-md px-2 py-1">
                                <IoChatboxEllipsesOutline size={13} className="text-color-300"/>
                                <span className="text-xs text-color-300 tracking-wide">{t('reply')}</span>
                            </div>
                        )}
                        {isCreator && (
                            <span onClick={toggleOpenEdit}
                                  className="flex items-center text-blue-500 hover:text-blue-700 cursor-pointer px-1 py-1">
                                <FiEdit3 size={12} className="mr-1"/>
                                <span className="font-medium text-xs italic tracking-wide">{t('edit')}</span>
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Replies */}
            {openReplies && (
                <div className="ml-8 mt-2 pl-3 border-l-2 border-color-50">
                    <Replies
                        comment_id={id}
                        id={item?.container_id}
                        type={item?.container_type}
                    />
                </div>
            )}
        </div>
    )
}

export const VoteComment = ({item, ref_id, className, canReply = true}) => {
    const t = useTranslate()
    const login = useContext(LoginContext)
    const {isSm} = useContext(LayoutContext)
    const {item: feed} = useContext(FeedContext)

    const [openEdit, toggleOpenEdit] = useToggle()

    const [openReplies, toggleOpenReplies] = useToggle()

    const {
        creator,
        created,
        message,
        container_id,
        container_type,
        vote_status,
        total_votes,
        total_downvotes,
        total_replies,
    } = item ?? {}

    const {name = '', avatar, thumbnail} = creator ?? {}

    const isDeleted = !!_.get(item, 'isDeleted', false)

    const mentions = Array.from(item?.mentions ?? [])

    const messageContent = useMemo(() => {
        return renderElse(
            isDeleted,
            <Description
                data={item}
                mentions={mentions}
                type={container_type}>
                {message}
            </Description>
        )
    }, [isDeleted, message, mentions])

    const showReplies = !isDeleted && !!canReply

    const Wrapper = !!canReply ? AskWrapper : renderOwnChild

    const hasExtraVote = !isDeleted

    const handleExtraVote = useExtraVote({
        vote_status,
        owner_id: getId(item),
        owner_type: getType(item),
    })

    const vote = useCallback(
        (value) => {
            if (isExtraVoting(vote_status) && value === vote_status) {
                handleExtraVote(VoteTypes.NONE)
            } else {
                handleExtraVote(value)
            }
        },
        [handleExtraVote, vote_status]
    )

    const isAuthor = getId(creator) === getId(feed?.creator)
    const isCreator = getId(creator) === getId(login)

    const get_decrypted_values = useCallback((values) => {
        const {message, mentions} = values ?? {}
        const description_data = get_mention_data(mentions) ?? undefined
        return {
            ...(values ?? {}),
            description_data,
            message: getText(message, get_mention_data(mentions, true)),
        }
    }, [])

    return (
        <Wrapper
            className="mb-4 pb-2"
            item={item}
            ref_id={ref_id}>
            <div className={classNames('pt-2 relative', className)}>
                <div className="flex items-center gap-2">
                    <Avatar
                        className="flex flex-center background-200"
                        size="small"
                        icon={
                            <RiUser3Fill
                                size={12}
                                className="text-color-400"
                            />
                        }
                        src={avatar || thumbnail}
                    />
                    <div className="leading-tight flex flex-col flex-1">
                        <Link
                            rel="nofollow"
                            to={getLinkToDetail(creator)}
                            className="flex items-end gap-2 no-underline">
                            <span className={classNames("font-bold text-sm", isAuthor && 'italic')}>
                                {getTitle(creator)}
                            </span>
                        </Link>
                        <span className="text-2xs text-color-400 italic whitespace-no-wrap tracking-wide">
                            {formatSimplifyDate(created, 'fullTime')}
                        </span>
                    </div>
                    {!isDeleted && (
                        <ResponsesContextMenu
                            item={item}
                            onEdit={toggleOpenEdit}
                        />
                    )}
                </div>
                <div className="p-2">
                    {openEdit ? (
                        <ResponsesEditAction
                            onExit={toggleOpenEdit}
                            // item={get_decrypted_values(item)}
                            item={item}
                        />
                    ) : (
                        messageContent
                    )}
                </div>
                {!isDeleted && (
                    <div className="flex flex-col">
                        <div className="flex gap-6 mt-1">
                            {hasExtraVote && (
                                <div className="flex">
                                    <ExtraLike
                                        action={VoteTypes.UP}
                                        vote_status={vote_status}
                                        total_votes={total_votes ?? 0}
                                        onClick={() => vote(VoteTypes.UP)}
                                    />
                                    <ExtraLike
                                        action={VoteTypes.DOWN}
                                        vote_status={vote_status}
                                        onClick={() => vote(VoteTypes.DOWN)}
                                        total_downvotes={total_downvotes ?? 0}
                                    />
                                </div>
                            )}
                            {showReplies && (
                                <div
                                    onClick={isDeleted ? null : toggleOpenReplies}
                                    className="cursor-pointer flex items-center gap-1 font-medium text-sm text-color-400 hover:text-primary italic">
                                    <IoChatboxEllipsesOutline size={16}/>
                                    {renderIf(
                                        total_replies > 0,
                                        <span className="text-xs">{total_replies}</span>
                                    )}
                                </div>
                            )}
                            {renderIf(
                                isCreator,
                                <span onClick={() => {
                                    toggleOpenEdit()
                                }}
                                      className="flex items-center text-blue-500 hover:text-blue-700 cursor-pointer">
                                    <FiEdit3 size={13} className="mr-1"/>
                                    <span className="font-medium text-xs italic tracking-wide leading-tight">
                                        {t('edit')}
                                    </span>
                                </span>
                            )}
                        </div>
                        {showReplies && total_replies > 0 && (
                            <div
                                onClick={() => {
                                    toggleOpenReplies()
                                }}
                                className="ml-2 mt-2 flex items-center gap-2 text-xs italic cursor-pointer">
                                <BsReplyFill
                                    size={16}
                                    style={{color: '#dcdcdc', transform: 'rotate(180deg)'}}
                                />
                                <span className="flex-1 font-medium text-color-400 hover:text-color-100 cursor-pointer">
                                    {renderIf(
                                        total_replies > 0,
                                        <span className="font-bold text-color-000 mr-1">
                                          {total_replies}
                                        </span>
                                    )}
                                    {t('reply')}
                                </span>
                            </div>
                        )}
                        {openReplies && (
                            <div className="ml-2 mt-1">
                                <Replies
                                    id={container_id}
                                    type={container_type}
                                    comment_id={getId(item)}
                                />
                            </div>
                        )}

                    </div>
                )}
            </div>
        </Wrapper>
    )
}

const renderComment =
    ({id, type, canReply = true}) =>
        (item) => {
            // const isVoteComment = type === 'post' || type === 'ask'
            const isVoteComment = type === 'ask'
            const Component = isVoteComment ? VoteComment : Comment
            return (
                <Component
                    item={item}
                    key={item.id}
                    ref_id={id}
                    canReply={canReply}
                />
            )
        }

export const Responses = ({type, id, Feature = NullAsDefault, renderEmpty = NullAsDefault}) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)

    const dispatch = useDispatch()

    const {socket, authenticated} = useContext(SocketContext)

    const [refreshKey, setRefreshKey] = useState(Date.now())

    const {sortByDate: comments = []} = useSelectEntities(
        `${type}_${id}`,
        commentContainerSchema,
        {}
    )

    const login = useContext(LoginContext)

    useEffect(() => {
        if (socket && type && id) {
            socket.emit(JOIN_ROOM, {
                roomId: id,
                roomType: type,
            })
        }
    }, [id, type, socket])

    useEffect(() => {
        if (!socket) return
        let timeout
        if (authenticated) {
            socket.off('server-push-content')
            socket.on('server-push-content', (data) => {
                dispatch({
                    type: 'entities',
                    ...normalize(data, socketUnionSchema),
                })
            })
        }
        return () => {
            if (timeout) {
                clearTimeout(timeout)
            }
        }
    }, [authenticated, dispatch, socket])

    const comment_ids = Object.keys(comments)
        .sort((a, b) => Number(b) - Number(a))
        .map((key) => comments[key])

    return (
        <React.Fragment>
            {!login ? (
                <div className="flex flex-col space-y-3 mb-16 mt-3">
                    <div className="flex flex-col flex-center gap-3 border border-dashed rounded-lg px-2 py-8">
                        <div className="text-center text-sm text-color-400 italic">
                            {t(
                                'Please log in to Kristo Network to leave your comment in the post'
                            )}
                        </div>
                        <Link
                            to={'/login'}
                            className="rounded-md px-3 py-1 bg-primary text-white text-sm no-text-shadow hover:text-white no-underline shadow-lg">
                            {t('Login now')}
                        </Link>
                    </div>
                    <GoogleAdsRecommendedUnitHorizontal/>
                </div>
            ) : (
                <div className="z-10">
                    {
                        !!type && !!id && (
                            <AsyncAction
                                onSuccess={() => {
                                    successNotify(NotificationActionTypes.CREATE, t)
                                    setRefreshKey(Date.now())
                                }}
                                apiInfo={comment_addComment_Api}
                                query={{
                                    ':prop': type,
                                    ':id': id,
                                }}>
                                {({isLoading, handleAsyncAction}) => (
                                    <InputBar
                                        key={refreshKey}
                                        textOnly={true}
                                        rows={5}
                                        isLoading={!!isLoading}
                                        defaultHeight={90}
                                        placeholder={t('type comment')}
                                        handleSubmit={({description, photos = []}) => {
                                            handleAsyncAction({
                                                message: description,
                                                photos
                                            })
                                        }}
                                    />
                                )}
                            </AsyncAction>
                        )
                    }
                </div>
            )}

            <div className="flex-1">
                {type === 'ask' && (
                    <Feature/>
                )}
                <div className="mt-6 mb-3">
                    <div className="pb-1 border-b border-color-100 flex items-center flex-1 gap-2">
                        <IoIosChatbubbles size={15} className="text-gray-500"/>
                        <span className="flex-1 font-medium text-sm text-gray-600 leading-tight tracking-widest">
                            {type === 'ask' ? t('answers') : t('comments')}
                        </span>
                    </div>
                </div>
                {
                    !!type && !!id && (
                        <LazyPagination
                            query={{
                                ':prop': type,
                                ':id': id,
                            }}
                            renderLoading={renderLoading}
                            apiInfo={comment_getComments_Api}
                            refreshKey={refreshKey}
                            key={comment_getComments_Api.path
                                .replace(':prop', type)
                                .replace(':id', id)}
                            renderEmpty={_.isEmpty(comment_ids) ? () => <EmptyHolder/> : NullAsDefault}
                            renderItem={(item) => (
                                <SelectEntityItem
                                    item={item?.id}
                                    defaultItem={item}
                                    schema={commentSchema}>
                                    {renderComment({id, type})}
                                </SelectEntityItem>
                            )}
                        />
                    )
                }
            </div>
        </React.Fragment>
    )
}

export const Replies = nest(({type, id, comment_id}) => {
    const t = useTranslate()
    const login = useContext(LoginContext)
    const [refreshKey, setRefreshKey] = useState(Date.now())
    const {sortByDate: comments = []} = useSelectEntities(
        `${type}_${id}_${comment_id}`,
        commentContainerSchema,
        {}
    )

    return (
        <React.Fragment>
            {!login ? (
                <div className="flex flex-col flex-center gap-3 border border-dashed rounded-lg px-2 py-8">
                    <div className="text-center text-sm text-color-400 italic">
                        {t(
                            'Please log in to Kristo Network to leave your comment in the post'
                        )}
                    </div>
                    <Link
                        to={'/login'}
                        className="rounded-md px-3 py-1 bg-primary text-white text-sm no-text-shadow hover:text-white no-underline shadow-lg">
                        {t('Login now')}
                    </Link>
                </div>
            ) : (
                <div className="z-10">
                    <AsyncAction
                        onSuccess={() => {
                            successNotify(NotificationActionTypes.CREATE, t)
                            setRefreshKey(Date.now())
                        }}
                        apiInfo={comment_addReply_Api}
                        query={{
                            ':type': type,
                            ':id': id,
                            ':comment_id': comment_id,
                        }}>
                        {({isLoading, handleAsyncAction}) => {
                            return (
                                <InputBar
                                    defaultHeight={90}
                                    key={refreshKey}
                                    textOnly={true}
                                    message={true}
                                    isLoading={!!isLoading}
                                    placeholder={t('reply')}
                                    // rows={2}
                                    className="bg-transparent"
                                    handleSubmit={logFunc(({description}) => {
                                        if (!description?.length) {
                                            return
                                        }
                                        handleAsyncAction({
                                            message: description,
                                        })
                                    })}
                                />
                            )
                        }}
                    </AsyncAction>
                </div>
            )}
            <div className="mt-2 flex-1 verticalList">
                {Object.keys(comments)
                    .sort((a, b) => Number(b) - Number(a))
                    .map((key) => comments[key])
                    .map((key) => (
                        <SelectEntityItem
                            key={key}
                            item={key}
                            schema={commentSchema}>
                            {renderComment({id, type, canReply: false})}
                        </SelectEntityItem>
                    ))}
                <LazyPagination
                    renderEmpty={NullAsDefault}
                    query={{
                        ':type': type,
                        ':id': id,
                        ':comment_id': comment_id,
                    }}
                    apiInfo={comment_getReples_Api}
                    renderItem={(item) => (
                        <SelectEntityItem
                            item={item?.id}
                            defaultItem={item}
                            schema={commentSchema}>
                            {(item) => {
                                const ids = Object.values(comments)
                                const hideRecentComment = _.find(ids, (value) =>
                                    [_.get(item, 'id'), _.get(item, 'idname')].includes(value)
                                )
                                return hideRecentComment
                                    ? null
                                    : renderComment({id, type, canReply: false})(item)
                            }}
                        </SelectEntityItem>
                    )}
                />
            </div>
        </React.Fragment>
    )
})
